import NumberBox from 'devextreme-react/number-box';
import TextBox from 'devextreme-react/text-box';
import DateBox from 'devextreme-react/date-box';
import SelectBox from 'devextreme-react/select-box';
import React, { useCallback, useMemo } from 'react';
import { ColumnEditCellTemplateData } from 'devextreme/ui/data_grid';
import { SysIResultados } from '../../../../../types/types.orders';
import { Autocomplete, Button, Tooltip } from 'devextreme-react';
import { useAppSelector, useAppDispatch } from '../../../../../contexts/hooks';
import { selectModuleTracking } from '../../../../../contexts/selectors';
import { resGridConfidential, resGridImageResult, resGridTemplateEmpty, ordPending, agarMicrobiology, multilineIcon } from '../../../../../images/icons';
import { showModalTemplates, showModalImages, showModalMicrobiology, showModalMultiline, showModalImagenesDx } from '../../../../../contexts/slices/orders';
import { ResultTypeResult } from '../../../../../types/types.analitos';
import { isResultAltered } from '../Functions/resultStyles';

const timeLabel = { 'aria-label': 'Time' };
const dateLabel = { 'aria-label': 'Date' };

function DynamicEditCellComponent({ data }: { data: ColumnEditCellTemplateData<SysIResultados, Number> }): React.ReactElement {
  const { data: rowData, setValue, value } = data;
  const ModuleTracking: boolean = useAppSelector(selectModuleTracking);
  const Prm_Res_Conf = useAppSelector((x) => x.userInfo.Permissions?.Prm_Res_Conf);
  const Prm_ResC = useAppSelector((x) => x.userInfo.Permissions?.Prm_ResC) || false;
  const lstResulTipoListas = useAppSelector((x) => x.configuration.LstAnalitosResulTipoListas);
  const dispatch = useAppDispatch();

  // dataSource estable: solo se recalcula si cambia la lista o el analito de la fila.
  const dataSourceItems = useMemo(() => lstResulTipoListas.filter((x) => x.Rtl_An_Id === rowData?.Res_An_Id), [lstResulTipoListas, rowData?.Res_An_Id]);

  // Clase de "alterado" reactiva al valor, calculada con useMemo (sin useState +
  // useEffect, que provocaba un render extra de la celda en edición).
  const alteredData = useMemo(() => (isResultAltered(rowData) ? 'result-error' : ''), [rowData, value]);

  // Función para sanitizar cualquier valor que pueda ser un objeto
  const simpleValueChanged = React.useCallback(
    (e) => {
      setValue(e.value);
    },
    [setValue]
  );

  const handleModalTemplate = useCallback(() => {
    if (!rowData || !Prm_ResC) {
      return;
    }
    dispatch(showModalTemplates({ Visible: true, An_Id: rowData.Res_An_Id, An_Nombre: rowData.Analito.An_Nombre, Res_Id: rowData.Res_Id, Res_Estado: rowData.Res_Estado, Res_Resultado: rowData.Res_ResultadoPlantilla, An_Plantilla: rowData.Analito.An_Plantilla, Update: false, Pc_Id: rowData.Orden.Ord_Pc_Id }));
  }, []);

  const handleModalMicrobiology = useCallback(() => {
    if (!rowData || !Prm_ResC) {
      return;
    }
    dispatch(
      showModalMicrobiology({
        Visible: true,
        Ord_Id: rowData.Res_Ord_Id,
        Es_Id: rowData.Res_Es_Id,
        Es_Nombre: rowData.Estudio.Es_Nombre,
        An_Id: rowData.Res_An_Id,
        An_Nombre: rowData.Analito.An_Nombre,
        Res_Id: rowData.Res_Id,
        Res_Estado: rowData.Res_Estado,
        Res_Resultado: rowData.Res_Resultado,
        Update: false,
        Pc_Id: rowData.Orden.Ord_Pc_Id,
      })
    );
  }, [rowData, Prm_ResC, dispatch]);

  const handleModalMultiline = useCallback(() => {
    if (!rowData || !Prm_ResC) {
      return;
    }
    dispatch(
      showModalMultiline({
        Visible: true,
        An_Id: rowData.Res_An_Id,
        An_Nombre: rowData.Analito.An_Nombre,
        Res_Id: rowData.Res_Id,
        Res_Estado: rowData.Res_Estado,
        Res_Resultado: rowData.Res_Resultado,
        Update: false,
        Pc_Id: rowData.Orden.Ord_Pc_Id,
      })
    );
  }, [rowData, Prm_ResC, dispatch]);

  const handleModalImagenesDx = useCallback(() => {
    if (!rowData || !Prm_ResC) {
      return;
    }
    dispatch(showModalImagenesDx({ Visible: true, An_Id: rowData.Res_An_Id, An_Nombre: rowData.Analito.An_Nombre, Res_Id: rowData.Res_Id, Res_Estado: rowData.Res_Estado, Res_Resultado: rowData.Res_ResultadoPlantilla, An_Plantilla: rowData.Analito.An_Plantilla, Update: false, Pc_Id: rowData.Orden.Ord_Pc_Id, Ord_Id: rowData.Res_Ord_Id, Es_Id: rowData.Res_Es_Id }));
  }, []);

  const handleModalImage = useCallback(() => {
    if (!rowData || !Prm_ResC) {
      return;
    }
    dispatch(showModalImages({ Visible: true, An_Id: rowData.Res_An_Id, An_Nombre: rowData.Analito.An_Nombre, Res_Id: rowData.Res_Id, Res_Estado: rowData.Res_Estado, Res_Resultado: rowData.Res_ResultadoPlantilla, An_Plantilla: rowData.Analito.An_Plantilla, Update: false, Pc_Id: rowData.Orden.Ord_Pc_Id }));
  }, []);

  // Manejador para valores numéricos que permite > y <
  const handleNumericValueChanged = useCallback(
    (e) => {
      let newValue = e.value;

      if (newValue) {
        // Eliminar espacios
        newValue = String(newValue).replace(/\s/g, '');

        // Permitir solo caracteres válidos: números, un punto, > o < al inicio, - para negativos
        // Eliminar caracteres inválidos
        let cleaned = '';
        let dotFound = false;
        let symbolFound = false;
        let minusFound = false;

        for (let i = 0; i < newValue.length; i++) {
          const char = newValue[i];

          // > o < solo al inicio
          if ((char === '>' || char === '<') && i === 0 && !symbolFound) {
            cleaned += char;
            symbolFound = true;
            continue;
          }

          // - solo al inicio o después de > o <
          if (char === '-' && !minusFound && (i === 0 || (symbolFound && i === 1))) {
            cleaned += char;
            minusFound = true;
            continue;
          }

          // Números siempre permitidos
          if (char >= '0' && char <= '9') {
            cleaned += char;
            continue;
          }

          // Punto solo una vez
          if (char === '.' && !dotFound) {
            cleaned += char;
            dotFound = true;
            continue;
          }
        }

        setValue(cleaned);
      } else {
        setValue(newValue);
      }
    },
    [setValue]
  );

  // Manejador simple que solo bloquea letras y caracteres especiales no permitidos
  const handleNumericKeyDown = useCallback((e: any) => {
    const key = e.event.key;

    // Permitir teclas de control y navegación
    if (e.event.ctrlKey || e.event.metaKey || key === 'Backspace' || key === 'Delete' || key === 'Tab' || key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown' || key === 'Home' || key === 'End' || key === 'Enter' || key === 'Escape') {
      return;
    }

    // Permitir solo: números (0-9), punto (.), > , < y -
    const allowedPattern = /^[0-9.<>\-]$/;
    if (!allowedPattern.test(key)) {
      e.event.preventDefault();
      return;
    }
  }, []);

  //Eliminamos el render primero para las muestras que no están trackeadas
  if ((ModuleTracking && !rowData?.Res_MuestraTrackedada) || !rowData?.Res_EstadoMuestra) {
    return (
      <div>
        <img src={ordPending} id={`NoTracking${rowData?.Res_Id}`} alt='Muestra sin tracking' />
        <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position='right' target={`#NoTracking${rowData?.Res_Id}`}>
          {!rowData?.Res_EstadoMuestra ? 'Muestra sin confirmar' : 'Muestra sin tracking'}
        </Tooltip>
      </div>
    );
  }

  //Validamos los confidenciales
  if (rowData?.Estudio.Es_Confidencial && rowData.Res_Estado > 1 && !Prm_Res_Conf) {
    return (
      <div>
        <img src={resGridConfidential} id={`Confidential${rowData?.Res_Id}`} alt='Muestra sin tracking' />
        <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position='right' target={`#Confidential${rowData?.Res_Id}`}>
          Resultado confidencial
        </Tooltip>
      </div>
    );
  }

  // Protección adicional: si el value es el objeto problemático, manejarlo aquí
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    // Si es un objeto con las claves problemáticas, renderizar como JSON en un TextBox readonly
    const objectKeys = Object.keys(value);
    if (objectKeys.some((key) => key.includes('Superficiales') || key.includes('Parabasales'))) {
      return (
        <div className={`custom-editorText ${alteredData}`}>
          <TextBox defaultValue={JSON.stringify(value)} readOnly={true} />
        </div>
      );
    }
  }

  switch (rowData?.Analito.An_Tr_Id) {
    case ResultTypeResult.NUMERICO: //Numerico
    case ResultTypeResult.FORMULA: //Formula
      return (
        <div>
          <div className={`custom-editorText ${alteredData}`}>
            <TextBox defaultValue={value} showClearButton={true} readOnly={rowData.Res_Estado >= 3 || !Prm_ResC} onValueChanged={handleNumericValueChanged} onKeyDown={handleNumericKeyDown} />
          </div>
        </div>
      );
    case ResultTypeResult.TEXTUAL: //Textual
      return (
        <div>
          <div className={`custom-editorText ${alteredData}`}>
            <TextBox defaultValue={value} onValueChanged={simpleValueChanged} readOnly={rowData.Res_Estado >= 3 || !Prm_ResC}></TextBox>
          </div>
        </div>
      );
    case ResultTypeResult.PLANTILLA: //Plantilla
      let result = [];
      let isTemplate = false;
      if (rowData.Res_Resultado) {
        try {
          result = JSON.parse(rowData.Res_Resultado);
          isTemplate = true;
        } catch (error) {
          result = [];
          isTemplate = false;
        }
        if (!isTemplate) {
          return <div className={`custom-editorText ${alteredData}`}>{value}</div>;
        }
      }

      return (
        <div>
          <div>
            <Button id={`pltAn${rowData.Res_An_Id}`} icon={resGridTemplateEmpty} onClick={handleModalTemplate} />
            <Tooltip target={`#pltAn${rowData.Res_An_Id}`} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              {result.map((x: any) => {
                return (
                  <div key={String(x.Group) || 'unknown'} className='tooltip-content'>
                    <h6 className='tooltip-title'>{String(x.Group) || 'Sin nombre'}</h6>
                    {Array.isArray(x.Items) &&
                      x.Items.map((y: any) => {
                        // Verificar si y.Value es un objeto y manejarlo adecuadamente
                        if (typeof y.Value === 'object' && y.Value !== null) {
                          return (
                            <div key={String(y.Key) || Math.random()} className='tooltip-item'>
                              <strong>&nbsp;&nbsp;{String(y.Label) || 'N/A'}:</strong>
                              {Object.entries(y.Value).map(([key, value]) => (
                                <p key={String(key) || Math.random()} style={{ marginLeft: '20px', margin: '2px 0' }}>
                                  <strong>{String(key).trim() || 'N/A'}: </strong>
                                  <span>{typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value) || 'N/A'}</span>
                                </p>
                              ))}
                            </div>
                          );
                        } else {
                          // Si y.Value es un string o número, renderizar normalmente
                          return (
                            <p key={String(y.Key) || Math.random()} className='tooltip-item'>
                              <strong>&nbsp;&nbsp;{String(y.Label) || 'N/A'}: </strong>
                              <span>{String(y.Value) || 'N/A'}</span>
                            </p>
                          );
                        }
                      })}
                  </div>
                );
              })}
            </Tooltip>
          </div>
        </div>
      );
    case ResultTypeResult.FECHA: //Fecha
      return (
        <div>
          <div className='custom-editorText'>{rowData.Res_Estado >= 3 ? <TextBox defaultValue={value} onValueChanged={simpleValueChanged} readOnly={rowData.Res_Estado >= 3 || !Prm_ResC}></TextBox> : <DateBox readOnly={rowData.Res_Estado >= 3 || !Prm_ResC} defaultValue={value} inputAttr={dateLabel} displayFormat={'yyyy/M/d'} type='date' validationError={'Error de validación'} onValueChanged={simpleValueChanged} />}</div>
        </div>
      );
    case ResultTypeResult.TIEMPO: //Tiempo
      return (
        <div>
          <div className='custom-editorText'>{rowData.Res_Estado >= 3 ? <TextBox defaultValue={value} onValueChanged={simpleValueChanged} readOnly={rowData.Res_Estado >= 3 || !Prm_ResC}></TextBox> : <DateBox readOnly={rowData.Res_Estado >= 3 || !Prm_ResC} defaultValue={value} displayFormat={'H:mm aa'} inputAttr={timeLabel} type='time' onValueChanged={simpleValueChanged} />}</div>
        </div>
      );
    case ResultTypeResult.IMAGEN: //Imagen
      return (
        <div>
          <div>
            <Button icon={resGridImageResult} onClick={handleModalImage} />
          </div>
        </div>
      );
    case ResultTypeResult.LISTA: //Lista
      return (
        <div className='expanded-select-container'>
          <div>
            <SelectBox
              dataSource={dataSourceItems}
              readOnly={rowData.Res_Estado >= 3 || !Prm_ResC}
              searchEnabled={true}
              searchTimeout={0}
              searchExpr={'Rtl_Abreviatura'}
              showClearButton={true}
              searchMode='contains'
              displayExpr={'Rtl_Resultado'}
              valueExpr={'Rtl_Resultado'}
              defaultValue={value}
              onValueChanged={simpleValueChanged}
              dropDownOptions={{
                width: 'auto',
                minWidth: 300, // Ancho mínimo del dropdown
                maxWidth: 500, // Ancho máximo del dropdown
              }}
              className='expanded-select'
            />
          </div>
        </div>
      );
    case ResultTypeResult.MICROBIOLOGIA:
      return <Button icon={agarMicrobiology} onClick={handleModalMicrobiology} disabled={!Prm_ResC} />;
    case ResultTypeResult.AUTOCOMPLETAR: //Autocompletar
      return (
        <div className='expanded-select-container'>
          <div>
            <Autocomplete
              dataSource={dataSourceItems}
              readOnly={rowData.Res_Estado >= 3 || !Prm_ResC}
              wrapItemText={true}
              searchTimeout={0}
              defaultValue={value}
              onValueChanged={simpleValueChanged}
              valueExpr={'Rtl_Resultado'}
              itemRender={renderAutocomplete}
              showClearButton={true}
              dropDownOptions={{
                width: 'auto',
                minWidth: 300, // Ancho mínimo del dropdown
                maxWidth: 500, // Ancho máximo del dropdown
              }}
              className='expanded-select'
            />
          </div>
        </div>
      );
    case ResultTypeResult.MULTILINEA:
      return (
        <div className={alteredData}>
          <Button icon={multilineIcon} onClick={handleModalMultiline} disabled={!Prm_ResC} />
        </div>
      );
    case ResultTypeResult.IMAGENESDX:
      return (
        <div>
          <Button icon={resGridTemplateEmpty} onClick={handleModalImagenesDx} disabled={!Prm_ResC} />
        </div>
      );
    default:
      return <div>unkown type</div>;
  }
}

const renderAutocomplete = (data) => {
  return <span>{data.Rtl_Resultado}</span>;
};

export default React.memo(DynamicEditCellComponent);
